import styled from '@emotion/styled';

/**
 * Habillage « pilule » des champs d'un `<AddressInput/>`, à poser AUTOUR de
 * lui :
 *
 *   <AddressFieldsStyled>
 *     <AddressInput value={address} onChange={setAddress} error={...} />
 *   </AddressFieldsStyled>
 *
 * Pourquoi ici et pas dans `AddressInput` : le composant est partagé avec la
 * page profil, qui le veut en MUI nu. Le style appartient donc à l'appelant.
 * La technique est celle que les parcours emploient déjà pour leurs propres
 * champs (`TextfieldStyled`) : on cible des classes MUI descendantes, ce qui
 * traverse `AddressInput` sans qu'il ait à exposer la moindre props de style.
 *
 * Ce fichier est mutualisé volontairement : les parcours d'inscription
 * (RC aujourd'hui, inclusion et écoles ensuite) portent tous le même
 * habillage — bord à 30px et champs plus bas que la taille MUI par défaut —,
 * et un `AddressInput` laissé nu détonne immédiatement au milieu d'eux.
 */
export const AddressFieldsStyled = styled.div`
  & .MuiInputBase-root {
    border-radius: 30px;
  }

  /* Même hauteur que les champs voisins de l'étape : MUI met 16.5px 14px. */
  & .MuiOutlinedInput-input {
    padding: 12px 16px;
  }

  /* Le libellé non rétréci est positionné pour la hauteur MUI par défaut :
     dans un champ plus bas, il faut le remonter, sinon il déborde sous la
     bordure. Mêmes valeurs que TextfieldStyled, pour que les libellés
     s'alignent d'un champ à l'autre. */
  & .MuiInputLabel-root[data-shrink='false'] {
    transform: translate(14px, 14px) scale(1);
  }

  /* La liste déroulante Pays : MUI pose son propre rembourrage sur la racine
     ET sur l'input, avec une spécificité supérieure aux deux règles
     ci-dessus — d'où le doublement du & . Les 40px à droite réservent la
     place de la flèche, qui est en position absolue et passerait sinon
     par-dessus le texte. */
  && .MuiAutocomplete-root .MuiOutlinedInput-root {
    padding: 0 40px 0 0;
  }

  /* Sélecteur volontairement long : MUI cible cet input via
     .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input,
     et un simple doublement de & faisait jeu égal — donc perdait, l'ordre
     d'insertion tranchant en faveur de MUI. Vérifié dans le navigateur : sans
     ces trois niveaux, le champ Pays restait 9px plus bas que ses voisins. */
  && .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input {
    padding: 12px 16px;
  }
`;

/**
 * Variante pour les écrans encore en MUI v4 (`@material-ui/core`) : la
 * finalisation d'inscription inclusion et les inscriptions école
 * classic/partner.
 *
 * Elle ne touche PAS à la forme des champs, contrairement à l'habillage
 * « pilule » ci-dessus. Mesuré dans le navigateur sur `/login/classic` : un
 * `AddressInput` nu y rend des champs de 53,1px de haut, 4px de rayon et 14px
 * de police, quand les champs `@material-ui/core` voisins font 53,6px, 4px et
 * 14px. L'écart est invisible, et le champ Pays y tombe à la même hauteur que
 * ses trois voisins sans avoir besoin des correctifs d'`AddressFieldsStyled`.
 * La pilule, elle, détonnerait franchement : 30px de rayon au milieu de champs
 * carrés.
 *
 * Ne manque que le rythme vertical : les champs voisins portent
 * `margin="normal"` (16px au-dessus, 8px en dessous) quand `AddressInput` n'a
 * aucune marge propre — il se collait donc à 8px du champ précédent.
 */
export const AddressFieldsLegacyStyled = styled.div`
  margin: 16px 0 8px;
`;
